import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import DigestPreviewDialog from "../components/DigestPreviewDialog";
import BrandingRevenueTab from "../components/BrandingRevenueTab";
import {
  ClipboardList, Activity, UserPlus, UserX, Shield, CheckCircle,
  ShieldAlert, Flame, HeartPulse, AlertTriangle, FileEdit, Clock,
  LogIn, Globe, Monitor, Navigation, MapPin, Loader2, Eye, BadgeDollarSign,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const actionIcons = {
  role_change: Shield,
  user_deactivated: UserX,
  user_reactivated: UserPlus,
  alert_assigned: UserPlus,
  escalation_rules_updated: FileEdit,
  alert_created: ShieldAlert,
  alert_resolved: CheckCircle,
};

const actionColors = {
  role_change: "bg-blue-100 text-blue-800",
  user_deactivated: "bg-red-100 text-red-800",
  user_reactivated: "bg-green-100 text-green-800",
  alert_assigned: "bg-purple-100 text-purple-800",
  escalation_rules_updated: "bg-gray-100 text-gray-800",
  alert_created: "bg-orange-100 text-orange-800",
  alert_resolved: "bg-green-100 text-green-800",
};

const typeIcons = { security: ShieldAlert, fire: Flame, medical: HeartPulse, accident: AlertTriangle, banditry: AlertTriangle };

function formatTime(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function parseUA(ua) {
  if (!ua) return { browser: "Unknown", os: "" };
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) && !/Edg\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) && !/Chrome\//.test(ua) ? "Safari"
    : "Other";
  const os =
    /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad|iOS/.test(ua) ? "iOS"
    : /Linux/.test(ua) ? "Linux"
    : "";
  return { browser, os };
}

function SignInsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sign-ins?limit=100`, { withCredentials: true });
      setRows(res.data);
    } catch (err) {
      logging.error("Failed to fetch sign-ins", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const roleColor = (r) =>
    r === "system_admin" ? "bg-blue-100 text-blue-800"
    : r === "govt_admin" ? "bg-purple-100 text-purple-800"
    : "bg-gray-100 text-gray-700";

  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid="sign-ins-table">
      <ScrollArea className="max-h-[65vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 sticky top-0">
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">User</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden sm:table-cell">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden md:table-cell">IP</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden lg:table-cell">Device</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 w-36">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-gray-400 font-body">Loading...</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-gray-400 font-body">No sign-ins recorded yet</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const ua = parseUA(r.user_agent || "");
              return (
                <TableRow key={r.log_id} data-testid={`signin-${r.log_id}`}>
                  <TableCell className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <LogIn className="w-3.5 h-3.5 text-green-600" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 font-body truncate">{r.actor_name || "—"}</p>
                        <p className="text-[10px] text-gray-400 font-mono truncate max-w-[220px]">{r.email || "—"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2 px-3 hidden sm:table-cell">
                    {r.role && (
                      <Badge className={`text-[10px] px-1.5 py-0.5 rounded-sm ${roleColor(r.role)} hover:${roleColor(r.role).split(' ')[0]}`}>
                        {r.role.replace("_", " ")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2 px-3 text-xs text-gray-700 font-mono hidden md:table-cell">
                    {r.ip ? (
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-gray-400" />{r.ip}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="py-2 px-3 text-xs text-gray-600 font-body hidden lg:table-cell">
                    {r.user_agent ? (
                      <span className="flex items-center gap-1" title={r.user_agent}>
                        <Monitor className="w-3 h-3 text-gray-400" />{ua.browser}{ua.os ? ` · ${ua.os}` : ""}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="py-2 px-3 text-xs text-gray-400 font-body">{formatTime(r.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-logs?limit=100`, { withCredentials: true });
      setLogs(res.data);
    } catch (err) {
      logging.error("Failed to fetch audit logs", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid="audit-log-table">
      <ScrollArea className="max-h-[65vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 sticky top-0">
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">Actor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">Details</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 w-36">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-sm text-gray-400 font-body">Loading...</TableCell></TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-sm text-gray-400 font-body">No audit logs yet</TableCell></TableRow>
            )}
            {logs.map((log) => {
              const Icon = actionIcons[log.action] || ClipboardList;
              const colorClass = actionColors[log.action] || "bg-gray-100 text-gray-800";
              return (
                <TableRow key={log.log_id} data-testid={`audit-${log.log_id}`}>
                  <TableCell className="py-2 px-3">
                    <Badge className={`text-[10px] px-1.5 py-0.5 rounded-sm ${colorClass} hover:${colorClass.split(' ')[0]}`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {log.action.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 px-3 text-sm text-gray-800 font-body">{log.actor_name}</TableCell>
                  <TableCell className="py-2 px-3 text-xs text-gray-600 font-body max-w-[300px] truncate">{log.details}</TableCell>
                  <TableCell className="py-2 px-3 text-xs text-gray-400 font-body">{formatTime(log.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

function ActivityTab() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/activity/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      logging.error("Failed to fetch activity stats", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const maxAlerts = Math.max(...stats.map((s) => s.alerts_created), 1);

  return (
    <div className="space-y-4" data-testid="activity-stats">
      {loading && <p className="text-center py-12 text-sm text-gray-400 font-body">Loading...</p>}
      {!loading && stats.length === 0 && <p className="text-center py-12 text-sm text-gray-400 font-body">No activity data</p>}
      {stats.map((s) => (
        <div key={s.user_id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`activity-${s.user_id}`}>
          <div className="flex items-center gap-3 mb-3">
            <img
              src={s.picture || `https://ui-avatars.com/api/?name=${s.name || "U"}&background=111827&color=fff&size=32`}
              alt={s.name}
              className="w-8 h-8 rounded-full border border-gray-200"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 font-body truncate">{s.name}</p>
              <p className="text-[10px] text-gray-400 font-body">{s.email} &middot; {s.role}</p>
            </div>
            <div className="flex gap-3 text-center">
              <div>
                <p className="text-lg font-black text-gray-900 font-heading">{s.alerts_created}</p>
                <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Created</p>
              </div>
              <div>
                <p className="text-lg font-black text-green-600 font-heading">{s.alerts_resolved}</p>
                <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Resolved</p>
              </div>
              <div>
                <p className="text-lg font-black text-blue-600 font-heading">{s.alerts_assigned}</p>
                <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Assigned</p>
              </div>
            </div>
          </div>

          {/* Activity bar */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-black rounded-full transition-all duration-300"
              style={{ width: `${Math.max((s.alerts_created / maxAlerts) * 100, 2)}%` }}
            />
          </div>

          {/* By type breakdown */}
          <div className="flex gap-3">
            {["security", "fire", "medical", "accident", "banditry"].map((t) => {
              const Icon = typeIcons[t];
              const count = s.by_type?.[t] || 0;
              if (count === 0) return null;
              return (
                <span key={t} className="flex items-center gap-1 text-[10px] text-gray-500 font-body">
                  <Icon className="w-3 h-3" style={{ color: t === "security" ? "#EF4444" : t === "fire" ? "#F97316" : t === "medical" ? "#3B82F6" : t === "banditry" ? "#881337" : "#EAB308" }} />
                  {count}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatTile({ label, value, sub, accent = "text-gray-900" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`routing-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</p>
      <p className={`font-heading text-2xl font-black tracking-tighter mt-1 ${accent}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500 font-body mt-0.5">{sub}</p>}
    </div>
  );
}

function RoutingUsageTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/routing/usage`, { withCredentials: true });
      setData(r.data);
    } catch (err) { logging.error("routing usage", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  if (loading) {
    return <p className="text-xs text-gray-400 font-body py-12 text-center">Loading…</p>;
  }
  if (!data) {
    return <p className="text-xs text-gray-400 font-body py-12 text-center">Failed to load usage data</p>;
  }

  const estCost = data.est_month_cost_usd || 0;
  const estSavings = data.est_savings_usd || 0;
  const hitRate = data.cache_hit_rate_7d || 0;
  return (
    <div className="space-y-4" data-testid="routing-usage-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Today" value={data.today} sub={`${data.cache_hits_today || 0} from cache`} />
        <StatTile
          label="Cache hit · 7d"
          value={`${hitRate}%`}
          sub={`${data.cache_hits_7d || 0} of ${data.last_7_days} hits`}
          accent={hitRate >= 30 ? "text-emerald-700" : hitRate >= 10 ? "text-gray-900" : "text-gray-500"}
        />
        <StatTile
          label="Month-to-date"
          value={data.month_to_date}
          sub={`~$${estCost.toFixed(2)} · saved $${estSavings.toFixed(2)}`}
          accent="text-emerald-700"
        />
        <StatTile label="All time" value={data.total} sub={`@ $${data.est_cost_per_call_usd.toFixed(3)}/call`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 mb-3">
            <MapPin className="w-3.5 h-3.5 text-gray-700" />
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gray-700">Top Destinations · last 7 days</p>
          </div>
          {data.top_destinations.length === 0 ? (
            <p className="text-xs text-gray-400 font-body py-6 text-center">No routes yet</p>
          ) : (
            <ul className="space-y-1.5" data-testid="routing-top-destinations">
              {data.top_destinations.map((d) => (
                <li key={d.destination} className="flex items-center justify-between gap-2 text-xs font-body">
                  <span className="text-gray-700 truncate flex-1" title={d.destination}>{d.destination}</span>
                  <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 rounded-sm text-[10px] px-1.5 py-0">{d.count}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-1.5 mb-3">
            <Clock className="w-3.5 h-3.5 text-gray-700" />
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gray-700">Recent Calls</p>
          </div>
          {data.recent.length === 0 ? (
            <p className="text-xs text-gray-400 font-body py-6 text-center">No routes yet</p>
          ) : (
            <ScrollArea className="h-[240px] pr-2">
              <ul className="space-y-1.5" data-testid="routing-recent-calls">
                {data.recent.map((c, i) => (
                  <li key={`${c.created_at}-${c.destination}-${i}`} className="flex items-center gap-2 text-[11px] font-body" data-testid={`routing-recent-${i}`}>
                    <span className="font-mono text-gray-500 shrink-0">{new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {c.engine === "cache" && (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-sm text-[9px] px-1 py-0 shrink-0">cache</Badge>
                    )}
                    <span className="text-gray-700 truncate flex-1" title={c.destination}>{c.destination}</span>
                    {c.alerts_on_recommended > 0 && (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-sm text-[9px] px-1 py-0">
                        {c.alerts_on_recommended}
                      </Badge>
                    )}
                    <span className="text-gray-400 shrink-0">{c.took_ms} ms</span>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </div>
      </div>

      <p className="text-[10px] text-gray-400 font-body italic px-1">
        Cost estimate is rough — Google&apos;s Directions API is roughly $0.005-$0.01 per call (with traffic + alternatives) plus ~$0.003 in Claude tokens per advisory. Confirm against your GCP billing dashboard.
      </p>
    </div>
  );
}


function WeeklyDigestCard() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/digest/me`, { withCredentials: true });
      setPrefs(r.data);
    } catch { /* not admin or no email */ }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const toggleOptIn = async () => {
    if (!prefs) return;
    setBusy(true);
    try {
      const next = !prefs.opt_in;
      const r = await axios.post(`${API}/admin/digest/me`, { opt_in: next }, { withCredentials: true });
      setPrefs(r.data);
      toast.success(next ? t("digest.subscribed_toast") : t("digest.unsubscribed_toast"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("digest.could_not_update"));
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/digest/send-now`, {}, { withCredentials: true });
      if (r.data?.sent > 0) {
        toast.success(t("digest.sent_toast", { alerts: r.data.alerts, sanctions: r.data.sanctions, scans: r.data.scans }));
      } else {
        toast.error(t("digest.email_gateway_failed"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("digest.could_not_send"));
    } finally {
      setSending(false);
    }
  };

  if (!prefs) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4" data-testid="weekly-digest-card">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-2">
        <div className="flex items-start gap-2">
          <ClipboardList className="w-4 h-4 text-gray-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-700">{t("digest.title")}</p>
            <p className="text-[11px] text-gray-500 font-body mt-0.5">
              <Trans i18nKey="digest.schedule" components={{ strong: <strong /> }} />
            </p>
            <p className="text-[10px] text-gray-400 font-body mt-0.5" data-testid="digest-email">→ {prefs.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            data-testid="digest-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold font-body border border-gray-300 hover:bg-gray-50 text-gray-700"
          >
            <Eye className="w-3.5 h-3.5" /> {t("digest.preview")}
          </button>
          <button
            type="button"
            onClick={toggleOptIn}
            disabled={busy}
            data-testid="digest-opt-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold font-body transition-colors ${
              prefs.opt_in
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "border border-gray-300 hover:bg-gray-50 text-gray-700"
            } disabled:opacity-50`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : prefs.opt_in ? t("digest.subscribed") : t("digest.subscribe")}
          </button>
          <button
            type="button"
            onClick={sendNow}
            disabled={sending}
            data-testid="digest-send-now"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold font-body bg-black hover:bg-gray-800 text-white disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("digest.send_now")}
          </button>
        </div>
      </div>

      <DigestPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        email={prefs.email}
        showSubscribe={!prefs.opt_in}
        onSubscribe={async () => { await toggleOptIn(); setPreviewOpen(false); }}
        subscribing={busy}
        testIdPrefix="digest-preview"
      />
    </div>
  );
}



export default function AdminActivityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch { /* notifications API is best-effort — dashboard still loads without them */ }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-activity-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Activity className="w-7 h-7 text-gray-900" strokeWidth={2} />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("admin_activity.title")}</h1>
            <p className="text-sm text-gray-500 font-body">{t("admin_activity.subtitle")}</p>
          </div>
        </div>

        <WeeklyDigestCard />

        <Tabs defaultValue="audit" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-sm p-0.5">
            <TabsTrigger value="audit" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-audit">
              <ClipboardList className="w-4 h-4 mr-1.5" /> {t("admin_activity.tab_audit")}
            </TabsTrigger>
            <TabsTrigger value="sign-ins" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-sign-ins">
              <LogIn className="w-4 h-4 mr-1.5" /> {t("admin_activity.tab_signins")}
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-activity">
              <Activity className="w-4 h-4 mr-1.5" /> {t("admin_activity.tab_activity")}
            </TabsTrigger>
            <TabsTrigger value="routing" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-routing-usage">
              <Navigation className="w-4 h-4 mr-1.5" /> {t("admin_activity.tab_maps")}
            </TabsTrigger>
            <TabsTrigger value="branding" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-branding">
              <BadgeDollarSign className="w-4 h-4 mr-1.5" /> {t("admin_activity.tab_branding")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="audit"><AuditLogTab /></TabsContent>
          <TabsContent value="sign-ins"><SignInsTab /></TabsContent>
          <TabsContent value="activity"><ActivityTab /></TabsContent>
          <TabsContent value="routing"><RoutingUsageTab /></TabsContent>
          <TabsContent value="branding"><BrandingRevenueTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
